import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

import { isPreviewOrganization } from './preview-cleanup-rows'

import {
  createPreview,
  getPreview,
  Preview,
  PreviewStatus,
  TERMINAL_PREVIEW_STATUSES
} from '@/api/configuration-hub-client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { OrganizationPicker } from '@/modules/epilot-idp/organization-picker'
import { useAuth } from '@/providers'

const STEPS: { status: PreviewStatus; label: string }[] = [
  { status: 'creating_org', label: 'Creating the preview org' },
  { status: 'copying_configuration', label: 'Copying the configuration' },
  { status: 'inviting', label: 'Sending the invite' },
  { status: 'ready', label: 'Ready' }
]

const stepIndex = (status: PreviewStatus) =>
  STEPS.findIndex((step) => step.status === status)

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const errorMessage = (error: unknown) => {
  const response = (
    error as { response?: { data?: { error?: string; message?: string } } }
  )?.response?.data

  return (
    response?.error ??
    response?.message ??
    (error instanceof Error ? error.message : 'Unknown error')
  )
}

const Progress = ({ preview }: { preview: Preview }) => {
  const current = stepIndex(preview.status)

  return (
    <ol className="flex flex-col gap-2 text-sm">
      {STEPS.map((step, index) => {
        const done = preview.status === 'ready' || index < current
        const active = index === current && preview.status !== 'ready'

        return (
          <li className="flex items-center gap-2" key={step.status}>
            {done ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : active ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="h-4 w-4 rounded-full border" />
            )}
            <span className={done || active ? '' : 'text-muted-foreground'}>
              {step.label}
            </span>
          </li>
        )
      })}
      {preview.status === 'failed' && (
        <li className="flex items-start gap-2 text-destructive">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Failed: {preview.error ?? 'unknown error'}</span>
        </li>
      )}
    </ol>
  )
}

/**
 * Creates a preview org of any organization through configuration-hub-api:
 * a new org (organization_use "Preview") with a copy of the source org's
 * configuration. The person to invite gets the "your preview org is ready"
 * email. Shows progress until the preview is ready or failed.
 */
export const CreatePreviewDialog = ({
  open,
  onOpenChange,
  initialSourceOrgId,
  onReady
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialSourceOrgId?: string
  onReady?: () => void
}) => {
  const { userData } = useAuth()
  const ownEmail = userData?.attributes?.email ?? ''

  const [sourceOrgId, setSourceOrgId] = useState(initialSourceOrgId ?? '')
  const [name, setName] = useState('')
  const [email, setEmail] = useState(ownEmail)
  const [language, setLanguage] = useState<'de' | 'en'>('de')
  const [started, setStarted] = useState<Preview | undefined>()

  useEffect(() => {
    if (open) {
      setSourceOrgId(initialSourceOrgId ?? '')
      setName('')
      setEmail(ownEmail)
      setLanguage('de')
      setStarted(undefined)
    }
  }, [open, initialSourceOrgId, ownEmail])

  const create = useMutation({
    mutationFn: () =>
      createPreview({
        sourceOrgId,
        name: name.trim(),
        requester: { email: email.trim(), language }
      }),
    onSuccess: (preview) => setStarted(preview)
  })

  const progress = useQuery({
    queryKey: ['preview', started?.source_org_id, started?.id],
    queryFn: () =>
      getPreview({
        sourceOrgId: started!.source_org_id,
        previewId: started!.id
      }),
    enabled: Boolean(started),
    refetchInterval: (query) =>
      query.state.data && TERMINAL_PREVIEW_STATUSES.has(query.state.data.status)
        ? false
        : 5000
  })
  const preview = progress.data ?? started

  useEffect(() => {
    if (preview?.status === 'ready') onReady?.()
  }, [preview?.status, onReady])

  const canSubmit =
    Boolean(sourceOrgId) &&
    EMAIL_PATTERN.test(email.trim()) &&
    !create.isPending

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">Create preview org</DialogTitle>
          <DialogDescription>
            Creates a temporary org with a copy of the selected org&apos;s
            configuration (no customer data). The person below is invited as
            owner once the copy is done.
          </DialogDescription>
        </DialogHeader>

        {preview ? (
          <div className="flex flex-col gap-3">
            <div className="text-sm">
              <span className="font-medium">{preview.name}</span>
              {preview.preview_org_id && (
                <span className="text-muted-foreground">
                  {' '}
                  · org {preview.preview_org_id}
                </span>
              )}
            </div>
            <Progress preview={preview} />
            {preview.status === 'ready' && (
              <p className="text-sm text-muted-foreground">
                {preview.requested_by.email} got an email with the invite link.
                {preview.sync_status === 'partial' &&
                  ' Some resources could not be copied.'}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="preview_source_org">Copy of</Label>
              <OrganizationPicker
                filter={(org) => !isPreviewOrganization(org)}
                id="preview_source_org"
                onChange={setSourceOrgId}
                value={sourceOrgId}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="preview_name">Name (optional)</Label>
              <Input
                id="preview_name"
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. EEG blueprint v3.2 update"
                value={name}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex flex-1 flex-col gap-2">
                <Label htmlFor="preview_invite_email">Invite</Label>
                <Input
                  id="preview_invite_email"
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  value={email}
                />
              </div>
              <div className="flex w-28 flex-col gap-2">
                <Label htmlFor="preview_language">Email language</Label>
                <Select
                  onValueChange={(v) => setLanguage(v as 'de' | 'en')}
                  value={language}
                >
                  <SelectTrigger id="preview_language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {create.isError && (
              <p className="text-sm text-destructive">
                {errorMessage(create.error)}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {preview ? (
            <Button onClick={() => onOpenChange(false)} variant="outline">
              Close
            </Button>
          ) : (
            <Button
              disabled={!canSubmit}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Create preview
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
